import { useState, useEffect, useCallback } from 'react'
import type { Account, AccountType, Member } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { accountsApi, membersApi } from '../lib/api'
import { AccountModal, CreditCardTile } from '../components/AccountShared'
import { CsvImportModal } from '../components/CsvImportModal'
import { CardPaymentForecastPanel } from '../components/CardPaymentForecastPanel'
import { SortableCardGrid } from '../components/SortableCardGrid'
import { formatsForAccount } from '../lib/csv-import'
import { accountOrderItems, moveAccount } from '../lib/account-order'
import { btnPrimary, btnSecondary, pageSubtitle, pageTitle } from '../lib/ui'

const PAGE_TYPES: AccountType[] = ['credit_card']

/** 所有者が未設定のカードをまとめるグループのキー。メンバーIDと衝突しない値にする */
const NO_MEMBER_KEY = '__no_member__'

export default function CreditCards() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [csvAccount, setCsvAccount] = useState<Account | null>(null)
  const [sorting, setSorting] = useState(false)
  // カードの設定や明細を更新したら引き落とし予定も取り直す
  const [forecastKey, setForecastKey] = useState(0)

  const fetchAccounts = useCallback(() => {
    if (!accessToken) return
    setForecastKey((k) => k + 1)
    setLoading(true)
    accountsApi.list(accessToken, { includeArchived: true })
      .then((res) => setAccounts(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
  }, [accessToken, t])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  // カードを所有者ごとに並べるために使う。取得に失敗してもカード一覧は表示する
  useEffect(() => {
    if (!accessToken) return
    void membersApi.list(accessToken).then((res) => setMembers(res.data)).catch(() => undefined)
  }, [accessToken])

  // アーカイブの切り替えは編集モーダルの中から呼ばれる。失敗の理由はモーダル内に
  // 出したいので、ここでは受け取らず throw させる
  const handleArchive = async (account: Account) => {
    if (!accessToken) return
    await accountsApi.update(accessToken, account.id, { isArchived: !account.isArchived })
    fetchAccounts()
  }

  // 完全削除。利用明細が残っているカードはサーバーが 409 で拒否するので、
  // その理由は呼び出し元（編集モーダル）に投げてモーダル内に表示させる
  const handleDelete = async (account: Account) => {
    if (!accessToken) return
    await accountsApi.delete(accessToken, account.id, { permanent: true })
    fetchAccounts()
  }

  // 並べ替え。表示順は口座とカードで 1 本の並びなので、動かした結果を全体に反映してから
  // 通し番号で保存する。画面は先に更新し、保存に失敗したら取り直して元に戻す
  const handleMove = async (movingId: string, targetId: string) => {
    if (!accessToken) return
    const next = moveAccount(accounts, movingId, targetId)
    if (next === accounts) return
    setAccounts(next)
    try {
      await accountsApi.reorder(accessToken, { items: accountOrderItems(next) })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('accounts_reorder_error'))
      fetchAccounts()
    }
  }

  const pageAccounts = accounts.filter((a) => a.type === 'credit_card')
  const filteredAccounts = showArchived
    ? pageAccounts
    : pageAccounts.filter((a) => !a.isArchived)

  const bankAccounts = accounts.filter((a) => (a.type === 'checking' || a.type === 'savings') && !a.isArchived)
  const bankAccountNameById = new Map(bankAccounts.map((a) => [a.id, a.name]))

  // 残高がプラスのカードは、利用明細の無い期間の引き落としを「振替」にしてしまった疑いがある。
  // 総資産がその分だけ過大に出るため、直し方まで含めてページ上部で案内する
  const overpaidCards = filteredAccounts.filter((a) => a.balance > 0)

  // カードの残高は負債なのでマイナスで持つ。利用額として集計するときは符号を反転する
  const sumLimit = (list: Account[]) => list.reduce((sum, a) => sum + (a.creditLimit ?? 0), 0)
  const sumUsed = (list: Account[]) => list.reduce((sum, a) => sum - a.balance, 0)
  const utilizationOf = (list: Account[]) => {
    const limit = sumLimit(list)
    return limit > 0 ? (sumUsed(list) / limit) * 100 : null
  }

  const totalLimit = sumLimit(filteredAccounts)
  const totalUsed = sumUsed(filteredAccounts)
  const totalUtilization = utilizationOf(filteredAccounts)

  const memberIds = new Set(members.map((m) => m.id))
  // メンバーの表示順どおりに並べ、所有者未設定（と、既に消えたメンバーを指すカード）は最後
  const groups = [
    ...members.map((m) => ({ key: m.id, name: m.name, accounts: filteredAccounts.filter((a) => a.memberId === m.id) })),
    {
      key: NO_MEMBER_KEY,
      name: t('accounts_member_none_group'),
      accounts: filteredAccounts.filter((a) => !a.memberId || !memberIds.has(a.memberId)),
    },
  ].filter((g) => g.accounts.length > 0)

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={pageTitle}>{t('credit_cards_title')}</h1>
          <p className={pageSubtitle}>
            {t('credit_cards_total_limit')}:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatJPY(totalLimit)}</span>
            {' ・ '}
            {t('credit_cards_used')}:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatJPY(totalUsed)}</span>
            {totalUtilization != null && `（${totalUtilization.toFixed(1)}%）`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer dark:text-gray-300">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded"
            />
            {t('accounts_show_archived')}
          </label>
          <button
            onClick={() => setSorting((v) => !v)}
            className={sorting ? btnPrimary : btnSecondary}
          >
            {sorting ? t('accounts_reorder_done') : t('accounts_reorder')}
          </button>
          <button
            onClick={() => { setEditingAccount(null); setModalOpen(true) }}
            className={btnPrimary}
          >
            {t('credit_cards_add')}
          </button>
        </div>
      </div>

      {sorting && (
        <p className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300">
          {t('accounts_reorder_hint')}
        </p>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {overpaidCards.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-300">
          <p className="font-medium">
            &#9888; {overpaidCards.map((a) => a.name).join('、')}: {t('accounts_card_overpaid_warning')}
          </p>
          <p className="mt-1 text-xs">{t('accounts_card_overpaid_hint')}</p>
        </div>
      )}

      {/* 「引き落とし日にお金が足りない」は最初に気付きたいので、カード一覧より前に出す */}
      {!loading && <CardPaymentForecastPanel token={accessToken ?? ''} refreshKey={forecastKey} />}

      {loading ? (
        <div className="mt-4 flex justify-center py-12">
          <p className="text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="mt-4 py-12 text-center text-gray-400">
          <p className="text-sm">{t('credit_cards_no_result')}</p>
        </div>
      ) : (
        groups.map((group) => {
          const groupUtilization = utilizationOf(group.accounts)
          return (
            <div key={group.key} className="mt-4">
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                {group.name}
                <span className="ml-2 font-normal text-gray-400 dark:text-gray-500">
                  {t('credit_cards_total_limit')}: {formatJPY(sumLimit(group.accounts))}
                  {' ・ '}
                  {t('credit_cards_used')}: {formatJPY(sumUsed(group.accounts))}
                  {groupUtilization != null && `（${groupUtilization.toFixed(1)}%）`}
                </span>
              </h2>
              <SortableCardGrid items={group.accounts} sorting={sorting} onMove={handleMove}>
                {(account) => (
                  <CreditCardTile
                    account={account}
                    onEdit={(a) => { setEditingAccount(a); setModalOpen(true) }}
                    onCsvImport={formatsForAccount(account).length > 0 ? (a) => setCsvAccount(a) : undefined}
                    paymentAccountName={account.paymentAccountId ? bankAccountNameById.get(account.paymentAccountId) : undefined}
                    transactionsHref={`/transactions?accountId=${account.id}`}
                  />
                )}
              </SortableCardGrid>
            </div>
          )
        })
      )}

      <AccountModal
        open={modalOpen}
        initial={editingAccount}
        allowedTypes={PAGE_TYPES}
        bankAccounts={bankAccounts}
        defaultColor="#dc2626"
        titleAddKey="credit_cards_modal_add"
        titleEditKey="credit_cards_modal_edit"
        nameLabelKey="credit_cards_form_name"
        namePlaceholderKey="credit_cards_form_name_placeholder"
        onClose={() => setModalOpen(false)}
        onSaved={fetchAccounts}
        onDelete={handleDelete}
        onArchive={handleArchive}
        token={accessToken ?? ''}
      />

      <CsvImportModal
        open={csvAccount !== null}
        account={csvAccount}
        token={accessToken ?? ''}
        onClose={() => setCsvAccount(null)}
        onImported={fetchAccounts}
      />
    </div>
  )
}
